import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  StatsDayBucket,
  StatsDays,
  StatsHourBucket,
  StatsTopScript,
} from "@agentdash/shared";
import { STATS_DAYS } from "@agentdash/shared";
import { useStats } from "../../store/hooks.js";

// ── helpers ──────────────────────────────────────────────────────────────────

function fillDailyGaps(buckets: StatsDayBucket[], days: number): StatsDayBucket[] {
  const byDate = new Map(buckets.map((b) => [b.date, b]));
  const result: StatsDayBucket[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    result.push(
      byDate.get(key) ?? {
        date: key,
        ok_minutes: 0,
        err_minutes: 0,
        ok_count: 0,
        err_count: 0,
        unknown_count: 0,
      },
    );
  }
  return result;
}

function formatAxisDate(dateStr: string, days: StatsDays): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (days === 7) {
    return d.toLocaleDateString("en", { weekday: "short", timeZone: "UTC" });
  }
  return d.toLocaleDateString("en", { month: "short", day: "numeric", timeZone: "UTC" });
}

function formatMinutes(minutes: number): string {
  if (minutes < 1) return `${Math.round(minutes * 60)}s`;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function formatDurationMs(ms: number): string {
  return formatMinutes(ms / 60_000);
}

function tooltipStyle() {
  return {
    contentStyle: {
      background: "var(--panel)",
      border: "1px solid var(--border)",
      borderRadius: 4,
      fontSize: 12,
    },
    labelStyle: { color: "var(--text)", marginBottom: 4 },
    itemStyle: { color: "var(--muted)" },
  };
}

function axisProps() {
  return { tick: { fill: "var(--muted)", fontSize: 11 }, axisLine: false, tickLine: false };
}

// ── sub-components ───────────────────────────────────────────────────────────

function TimeRangeToggle({
  days,
  onChange,
}: {
  days: StatsDays;
  onChange: (d: StatsDays) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {STATS_DAYS.map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => onChange(d)}
          style={{
            background: days === d ? "var(--accent)" : "transparent",
            color: days === d ? "#0e1116" : "var(--muted)",
            border: "1px solid var(--border)",
            borderRadius: 4,
            padding: "4px 12px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {d}d
        </button>
      ))}
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ padding: "16px 20px" }}>
      <div
        style={{
          color: "var(--muted)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </div>
      <div style={{ marginTop: 6, fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </div>
    </div>
  );
}

function KpiCards({
  kpi,
}: {
  kpi: { total_runs: number; total_duration_ms: number; success_rate: number | null; top_script: string | null };
}) {
  return (
    <div
      className="panel"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 0,
        padding: 0,
      }}
    >
      <KpiCard label="Total runs" value={kpi.total_runs.toLocaleString()} />
      <div style={{ borderLeft: "1px solid var(--border)" }}>
        <KpiCard label="Compute time" value={formatDurationMs(kpi.total_duration_ms)} />
      </div>
      <div style={{ borderLeft: "1px solid var(--border)" }}>
        <KpiCard
          label="Success rate"
          value={
            kpi.success_rate === null ? (
              <span style={{ color: "var(--muted)", fontSize: 14 }}>—</span>
            ) : (
              <span style={{ color: kpi.success_rate >= 90 ? "var(--ok)" : kpi.success_rate >= 70 ? "var(--warn)" : "var(--err)" }}>
                {kpi.success_rate}%
              </span>
            )
          }
        />
      </div>
      <div style={{ borderLeft: "1px solid var(--border)" }}>
        <KpiCard
          label="Most active script"
          value={
            kpi.top_script ? (
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: "var(--font-mono, monospace)",
                  wordBreak: "break-all",
                }}
              >
                {kpi.top_script}
              </span>
            ) : (
              <span style={{ color: "var(--muted)", fontSize: 14 }}>—</span>
            )
          }
        />
      </div>
    </div>
  );
}

function ChartPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="panel" style={{ padding: "16px 20px" }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: "var(--muted)",
          marginBottom: 16,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function DailyRuntimeChart({ daily, days }: { daily: StatsDayBucket[]; days: StatsDays }) {
  const tickInterval = days === 90 ? 6 : days === 30 ? 2 : 0;
  const data = daily.map((d) => ({
    ...d,
    label: formatAxisDate(d.date, days),
    total_minutes: d.ok_minutes + d.err_minutes,
  }));
  return (
    <ChartPanel title="Compute time per day (minutes)">
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} barSize={days === 7 ? 24 : undefined}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis dataKey="label" interval={tickInterval} {...axisProps()} />
          <YAxis
            {...axisProps()}
            tickFormatter={(v: number) => (v >= 60 ? `${Math.round(v / 60)}h` : `${v}m`)}
          />
          <Tooltip
            {...tooltipStyle()}
            formatter={(val, name) => [
              formatMinutes(Number(val)),
              name === "ok_minutes" ? "OK" : "Error",
            ]}
            labelFormatter={(label) => label}
          />
          <Bar dataKey="ok_minutes" stackId="a" fill="var(--ok)" name="ok_minutes" />
          <Bar
            dataKey="err_minutes"
            stackId="a"
            fill="var(--err)"
            name="err_minutes"
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
}

function DailyCountChart({ daily, days }: { daily: StatsDayBucket[]; days: StatsDays }) {
  const tickInterval = days === 90 ? 6 : days === 30 ? 2 : 0;
  const data = daily.map((d) => ({
    ...d,
    label: formatAxisDate(d.date, days),
  }));
  return (
    <ChartPanel title="Runs per day">
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} barSize={days === 7 ? 24 : undefined}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis dataKey="label" interval={tickInterval} {...axisProps()} />
          <YAxis {...axisProps()} allowDecimals={false} />
          <Tooltip
            {...tooltipStyle()}
            formatter={(val, name) => [
              val,
              name === "ok_count" ? "OK" : name === "err_count" ? "Error" : "Unknown",
            ]}
          />
          <Bar dataKey="ok_count" stackId="a" fill="var(--ok)" name="ok_count" />
          <Bar dataKey="err_count" stackId="a" fill="var(--err)" name="err_count" />
          <Bar
            dataKey="unknown_count"
            stackId="a"
            fill="var(--muted)"
            name="unknown_count"
            radius={[2, 2, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
}

function SuccessRateChart({ daily }: { daily: StatsDayBucket[] }) {
  const data = daily
    .filter((d) => d.ok_count + d.err_count > 0)
    .map((d) => ({
      label: d.date.slice(5),
      rate: Math.round((d.ok_count / (d.ok_count + d.err_count)) * 100),
    }));
  if (data.length === 0) {
    return (
      <ChartPanel title="Success rate (%)">
        <div style={{ color: "var(--muted)", fontSize: 13, padding: "16px 0" }}>No data</div>
      </ChartPanel>
    );
  }
  return (
    <ChartPanel title="Success rate (%)">
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis dataKey="label" {...axisProps()} />
          <YAxis {...axisProps()} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
          <Tooltip
            {...tooltipStyle()}
            formatter={(val) => [`${Number(val)}%`, "Success rate"]}
          />
          <Line
            type="monotone"
            dataKey="rate"
            stroke="var(--ok)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: "var(--ok)" }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
}

function TopScriptsChart({ top_scripts }: { top_scripts: StatsTopScript[] }) {
  if (top_scripts.length === 0) {
    return (
      <ChartPanel title="Top scripts by compute time">
        <div style={{ color: "var(--muted)", fontSize: 13, padding: "16px 0" }}>No data</div>
      </ChartPanel>
    );
  }
  const data = [...top_scripts].reverse();
  return (
    <ChartPanel title="Top scripts by compute time">
      <ResponsiveContainer width="100%" height={Math.max(160, data.length * 32)}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
          <XAxis
            type="number"
            {...axisProps()}
            tickFormatter={(v: number) => (v >= 60 ? `${Math.round(v / 60)}h` : `${v}m`)}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={160}
            tick={{ fill: "var(--muted)", fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: string) => (v.length > 22 ? `…${v.slice(-20)}` : v)}
          />
          <Tooltip
            {...tooltipStyle()}
            formatter={(val, _name, entry) => {
              const script = entry.payload as unknown as StatsTopScript;
              return [`${formatMinutes(Number(val))} (${script?.run_count ?? 0} runs)`, "Compute time"];
            }}
          />
          <Bar dataKey="total_minutes" fill="var(--accent)" radius={[0, 2, 2, 0]}>
            {data.map((_, i) => (
              <Cell key={i} fill="var(--accent)" />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
}

function HourlyHeatmap({ hourly }: { hourly: StatsHourBucket[] }) {
  const maxCount = Math.max(...hourly.map((h) => h.count), 1);
  return (
    <ChartPanel title="Activity by hour (UTC)">
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={hourly}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="hour"
            {...axisProps()}
            tickFormatter={(h: number) => `${h}h`}
            interval={3}
          />
          <YAxis {...axisProps()} allowDecimals={false} />
          <Tooltip
            {...tooltipStyle()}
            formatter={(val) => [val, "Runs"]}
            labelFormatter={(label) => `${Number(label)}:00–${Number(label)}:59 UTC`}
          />
          <Bar dataKey="count" radius={[2, 2, 0, 0]}>
            {hourly.map((h) => (
              <Cell
                key={h.hour}
                fill={`rgba(88, 166, 255, ${0.15 + 0.85 * (h.count / maxCount)})`}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
}

// ── page root ────────────────────────────────────────────────────────────────

export function Stats() {
  const [days, setDays] = useState<StatsDays>(30);
  const q = useStats(days);

  const filled = q.data ? fillDailyGaps(q.data.daily, days) : [];

  return (
    <div style={{ padding: 16, display: "grid", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Statistics</h2>
        <TimeRangeToggle days={days} onChange={setDays} />
      </div>

      {q.isError && (
        <div className="panel" style={{ padding: 16, color: "var(--err)" }}>
          Failed to load stats: {String((q.error as Error).message ?? q.error)}
        </div>
      )}

      {q.isLoading && (
        <div style={{ color: "var(--muted)", padding: 16 }}>Loading…</div>
      )}

      {q.data && (
        <>
          <KpiCards kpi={q.data.kpi} />
          <DailyRuntimeChart daily={filled} days={days} />
          <DailyCountChart daily={filled} days={days} />
          <SuccessRateChart daily={filled} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
              gap: 12,
            }}
          >
            <TopScriptsChart top_scripts={q.data.top_scripts} />
            <HourlyHeatmap hourly={q.data.hourly} />
          </div>
        </>
      )}
    </div>
  );
}
